import i18n from 'i18next';
import {ShadowType} from '../../../../incase-domain/common/elementShadow';
import {ImageFitEnum} from '../../../../incase-domain/common/imageFit';
import {IElement} from '../../../../incase-domain/elements';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import {IElementButton} from '../../../../incase-domain/elements/button';
import {IElementForm, ContentType, FormMethod} from '../../../../incase-domain/elements/form';
import {IElementHtml} from '../../../../incase-domain/elements/html/html';
import {IElementImage} from '../../../../incase-domain/elements/image';
import {IElementTextInput} from '../../../../incase-domain/elements/textInput';
import {IElementLineV2} from '../../../../incase-domain/elements/shapes/lineV2';
import {IElementRectangle} from '../../../../incase-domain/elements/shapes/rectangle';
import {IPresentationTheme, QuestionElement} from '../../../../incase-domain/presentationTheme';
import {IElementImageMarkup} from '../../../../incase-domain/elements/question/imageMarkup';
import {IElementLikertScale} from '../../../../incase-domain/elements/question/likertScale';
import {IElementMultiSelect} from '../../../../incase-domain/elements/question/multiSelect';
import {IElementPhotoAnswerQuestion} from '../../../../incase-domain/elements/question/photoAnswerQuestion';
import {IElementQuestion} from '../../../../incase-domain/elements/question/question';
import {IElementQuestionMatchPair, MatchPairQuestionMode} from '../../../../incase-domain/elements/question/questionMatchPair';
import {IElementSequenceQuestion} from '../../../../incase-domain/elements/question/questionSequence';
import {IElementQuestionYesNoV2} from '../../../../incase-domain/elements/question/questionYesNoV2';
import {getEffectiveQuestionTheme, questionElementTypeToThemeKey} from '../../../../utils/presentationTheme';
import logo from '../../../../../assets/logo.png';
import {THEME_QUESTION_PREVIEW_ID, buildThemeQuestionPreviewElement} from './buildThemeQuestionPreviewElement';
import {getSelectedThemeFont, themeElementFrame} from './themeStyle';

export function buildThemePreviewElement(elementType: ELEMENT, presentationTheme: IPresentationTheme): IElement | null {
    const effective = getEffectiveQuestionTheme(presentationTheme.elementStyles, elementType);

    if (elementType === ELEMENT.QUESTION) {
        return buildThemeQuestionPreviewElement(presentationTheme, effective);
    }

    const theme: QuestionElement = effective ?? {};
    const selectedFont = theme.fontFamily || getSelectedThemeFont(presentationTheme);

    const answerLabel = i18n.t('presEditPage.editSidebar.elements.initialStates.answer');
    const questionLabel = i18n.t('presEditPage.editSidebar.elements.initialStates.question');

    const base = {
        ...themeElementFrame(`${THEME_QUESTION_PREVIEW_ID}-${elementType}`, elementType, selectedFont),
        question: questionLabel,
        correctAnswers: [],
        haveTryButton: true,
        tryLabel: 'Confirm',
        showAllComments: false,
        showInReport: true,
        fitContent: true,
        writeAnswerIndices: true,
        fontFamily: selectedFont,
        fontSize: theme.answerFontSize?.value ?? 1.2,
        questionFontSize: theme.questionFontSize?.value ?? 1.4,
        commentFontSize: theme.commentFontSize?.value ?? 1,
        questionBackgroundColor: theme.questionBackgroundColor,
        questionBackgroundBorderRadius: theme.questionBackgroundBorderRadius,
        questionTextAlign: theme.questionTextAlign,
        correctColor: theme.correctColor,
        incorrectColor: theme.incorrectColor,
        debatableColor: theme.debatableColor,
        correctCommentColor: theme.correctCommentColor,
        incorrectCommentColor: theme.incorrectCommentColor,
        debatableCommentColor: theme.debatableCommentColor,
        borderRadius: theme.borderRadius,
        boxShadow: theme.boxShadow ?? ShadowType.MEDIUM,
    };

    switch (elementType) {
        case ELEMENT.YES_NO_QUESTION_V2:
            return {
                ...base,
                type: ELEMENT.YES_NO_QUESTION_V2,
                answers: [
                    {text: 'Yes', type: 'correct', comment: ''},
                    {text: 'No', type: 'incorrect', comment: ''},
                ],
                singleCorrect: true,
                writeAnswerIndices: false,
                fontSize: theme.answerFontSize?.value ?? 1.2,
                questionFontSize: theme.questionFontSize?.value ?? 1.2,
                commentFontSize: theme.commentFontSize?.value ?? 1.2,
            } as IElementQuestionYesNoV2;
        case ELEMENT.MULTI_SELECT:
            return {
                ...base,
                type: ELEMENT.MULTI_SELECT,
                font: {size: 1, color: '#000000'},
                answers: [
                    {text: `${answerLabel} 1`, type: 'correct', comment: ''},
                    {text: `${answerLabel} 2`, type: 'incorrect', comment: ''},
                ],
                singleCorrect: false,
                showCorrect: true,
                showWrong: true,
                showCorrectCount: true,
                showWrongCount: true,
            } as IElementMultiSelect;
        case ELEMENT.PHOTO_ANSWER_QUESTION:
            return {
                ...base,
                type: ELEMENT.PHOTO_ANSWER_QUESTION,
                blockingQuestion: true,
                imageFit: ImageFitEnum.COVER,
                answers: [
                    {text: `${answerLabel} 1`, type: 'correct', comment: '', imgSrc: ''},
                    {text: `${answerLabel} 2`, type: 'incorrect', comment: '', imgSrc: ''},
                ],
                answersInARow: 3,
                singleCorrect: false,
                fontSize: theme.answerFontSize?.value ?? 1.2,
                questionFontSize: theme.questionFontSize?.value ?? 1.4,
                commentFontSize: theme.commentFontSize?.value ?? 1,
            } as IElementPhotoAnswerQuestion;
        case ELEMENT.SEQUENCE_QUESTION:
            return {
                ...base,
                type: ELEMENT.SEQUENCE_QUESTION,
                blockingQuestion: true,
                sequence: [
                    {text: `${answerLabel} 1`, type: 'correct', comment: ''},
                    {text: `${answerLabel} 2`, type: 'correct', comment: ''},
                ],
                correctSequence: [`${answerLabel} 1`, `${answerLabel} 2`],
                automaticShuffle: true,
                singleCorrect: false,
            } as IElementSequenceQuestion;
        case ELEMENT.MATCH_PAIR_QUESTION:
            return {
                ...base,
                type: ELEMENT.MATCH_PAIR_QUESTION,
                blockingQuestion: true,
                matchPairMode: MatchPairQuestionMode.CARDS,
                leftElement: [
                    {id: 'theme-left-1', text: 'Element A1'},
                    {id: 'theme-left-2', text: 'Element A2'},
                ],
                rightElement: [
                    {id: 'theme-right-1', text: 'Element B1'},
                    {id: 'theme-right-2', text: 'Element B2'},
                ],
                pairs: [
                    {leftElementId: 'theme-left-1', rightElementId: 'theme-right-1'},
                    {leftElementId: 'theme-left-2', rightElementId: 'theme-right-2'},
                ],
                singleCorrect: false,
                selectedColor: theme.selectedColor,
                fontSize: theme.answerFontSize?.value ?? 1,
                questionFontSize: theme.questionFontSize?.value ?? 1.4,
                commentFontSize: theme.commentFontSize?.value ?? 0.9,
            } as IElementQuestionMatchPair;
        case ELEMENT.LIKERT_SCALE:
            return {
                ...base,
                type: ELEMENT.LIKERT_SCALE,
                scaleSize: 5,
                questions: [
                    {id: 'theme-likert-1', text: questionLabel},
                    {id: 'theme-likert-2', text: `${questionLabel} 2`},
                ],
                scale: [
                    {id: 'theme-scale-1', text: '1'},
                    {id: 'theme-scale-2', text: '2'},
                    {id: 'theme-scale-3', text: '3'},
                    {id: 'theme-scale-4', text: '4'},
                    {id: 'theme-scale-5', text: '5'},
                ],
                sliderView: true,
                singleCorrect: false,
                fontSize: theme.answerFontSize?.value ?? 1,
                questionFontSize: theme.questionFontSize?.value ?? 1.4,
                commentFontSize: theme.commentFontSize?.value ?? 1,
            } as IElementLikertScale;
        case ELEMENT.IMAGE_MARKUP:
            return {
                ...base,
                type: ELEMENT.IMAGE_MARKUP,
                imageSrc: '',
                blockingQuestion: true,
                markedAreas: [],
                intersectionAnswerType: false,
                correctAnswerMinIntersection: 0.5,
                widthHeightRatio: 1,
                isDebatable: false,
                singleCorrect: false,
                fontSize: theme.answerFontSize?.value ?? 1.2,
                questionFontSize: theme.questionFontSize?.value ?? 1.4,
                commentFontSize: theme.commentFontSize?.value ?? 1,
            } as IElementImageMarkup;
        default:
            return null;
    }
}

export function isThemePreviewableQuestionType(elementType: ELEMENT): boolean {
    return (
        elementType === ELEMENT.QUESTION ||
        elementType === ELEMENT.YES_NO_QUESTION_V2 ||
        elementType === ELEMENT.MULTI_SELECT ||
        elementType === ELEMENT.PHOTO_ANSWER_QUESTION ||
        elementType === ELEMENT.SEQUENCE_QUESTION ||
        elementType === ELEMENT.MATCH_PAIR_QUESTION ||
        elementType === ELEMENT.LIKERT_SCALE ||
        elementType === ELEMENT.IMAGE_MARKUP
    );
}

export type ThemePreviewQuestionElement =
    | IElementQuestion
    | IElementQuestionYesNoV2
    | IElementMultiSelect
    | IElementPhotoAnswerQuestion
    | IElementSequenceQuestion
    | IElementQuestionMatchPair
    | IElementLikertScale
    | IElementImageMarkup;

export function buildThemeQuestionControlElement(elementType: ELEMENT, presentationTheme: IPresentationTheme): IElementQuestion {
    return buildThemeQuestionPreviewElement(presentationTheme, getEffectiveQuestionTheme(presentationTheme.elementStyles, elementType));
}

export function buildThemeControlElement(elementType: ELEMENT, presentationTheme: IPresentationTheme): IElement {
    if (questionElementTypeToThemeKey[elementType]) {
        return buildThemeQuestionControlElement(elementType, presentationTheme);
    }

    const selectedFont = getSelectedThemeFont(presentationTheme);
    const id = `presentation-theme-${elementType}-preview`;
    const frame = themeElementFrame(id, elementType, selectedFont);

    if (elementType === ELEMENT.HTML) {
        const htmlTheme = presentationTheme.elementStyles?.htmlElement;
        const color = htmlTheme?.color || 'gray';
        const background = htmlTheme?.backgroundColor || 'transparent';
        const fontSize = htmlTheme?.fontSize?.value ?? 1.5;
        return {
            ...frame,
            type: ELEMENT.HTML,
            text: `<div style="font-size: ${fontSize}em; color: ${color}; background: ${background}">${i18n.t(
                'presEditPage.editSidebar.elements.initialStates.html'
            )}</div>`,
            scriptText: '',
            fontColor: htmlTheme?.color || '',
            color: htmlTheme?.backgroundColor || '',
            slideChange: {slideId: '', name: ''},
        } as IElementHtml;
    }

    if (elementType === ELEMENT.IMAGE) {
        return {
            ...frame,
            type: ELEMENT.IMAGE,
            imageSrc: logo,
            title: '',
            widthHeightRatio: 16 / 10,
            size: 1,
            borderRadius: presentationTheme.elementStyles?.imageElement?.borderRadius,
            slideChange: {slideId: '', name: ''},
        } as IElementImage;
    }

    if (elementType === ELEMENT.BUTTON) {
        const buttonTheme = presentationTheme.elementStyles?.buttonElement;
        return {
            ...frame,
            type: ELEMENT.BUTTON,
            text: i18n.t('presEditPage.editSidebar.elements.initialStates.button'),
            color: buttonTheme?.backgroundColor || '#41088A',
            htmlType: 'button',
            font: {color: buttonTheme?.color || '#fff', size: buttonTheme?.fontSize?.value || 1.8},
            slideChange: {slideId: '', name: ''},
        } as IElementButton;
    }

    if (elementType === ELEMENT.TEXT_INPUT) {
        const textInputTheme = presentationTheme.elementStyles?.textInputElement;
        return {
            ...frame,
            type: ELEMENT.TEXT_INPUT,
            label: i18n.t('presEditPage.editSidebar.elements.initialStates.textInputLabel'),
            placeholder: i18n.t('presEditPage.editSidebar.elements.initialStates.textInputPlaceholder'),
            input: '',
            validationType: '',
            htmlName: 'theme-preview-input',
            font: {color: '#000000', size: textInputTheme?.fontSize?.value ?? 1.5},
        } as IElementTextInput;
    }

    if (elementType === ELEMENT.FORM) {
        const formTheme = presentationTheme.elementStyles?.formElement;
        const textInput = buildThemeControlElement(ELEMENT.TEXT_INPUT, presentationTheme) as IElementTextInput;
        const button = buildThemeControlElement(ELEMENT.BUTTON, presentationTheme) as IElementButton;
        return {
            ...frame,
            type: ELEMENT.FORM,
            title: 'Form',
            fieldElements: [
                {...textInput, formWidth: 1},
                {...button, formWidth: 1},
            ],
            method: FormMethod.POST,
            submitUrl: '',
            fontSize: formTheme?.fontSize?.value ?? 1.5,
            target: '_blank',
            contentType: ContentType.JSON,
        } as IElementForm;
    }

    if (elementType === ELEMENT.LINE_V2 || elementType === ELEMENT.ARROW_V2) {
        const lineTheme = presentationTheme.elementStyles?.lineElement;
        return {
            ...frame,
            type: ELEMENT.LINE_V2,
            color: lineTheme?.color || '',
            size: lineTheme?.thickness ?? 0.1,
            startPoint: {x: 0.1, y: 0.5},
            endPoint: {x: 0.9, y: 0.5},
            slideChange: {slideId: '', name: ''},
        } as IElementLineV2;
    }

    if (
        elementType === ELEMENT.RECTANGLE ||
        elementType === ELEMENT.ELLIPSE ||
        elementType === ELEMENT.TRIANGLE ||
        elementType === ELEMENT.STAR ||
        elementType === ELEMENT.CIRCLE
    ) {
        const shapeTheme = presentationTheme.elementStyles?.shapeElement;
        return {
            ...frame,
            type: ELEMENT.RECTANGLE,
            color: shapeTheme?.color || '',
            fill: shapeTheme?.fill ?? false,
            slideChange: {slideId: '', name: ''},
        } as IElementRectangle;
    }

    return frame as IElement;
}
